import { useCallback, useEffect, useRef, useState } from 'react'

import { ConnectorBleDevice } from '@/constants/medconnect-devices-types'
import { discoverBluetoothDevices } from '@/core/classes/MedconnectDevicesAPI'

const SCAN_TIMEOUT_MS = 8000
/** A scan that returns faster than this did not really run (old connector, error): pause before retrying so we don't hammer it. */
const MIN_SCAN_INTERVAL_MS = 3000

export type BluetoothScanState = {
  devices: ConnectorBleDevice[]
  isScanning: boolean
  hasScanned: boolean
  rescan: () => void
}

/**
 * Repeatedly ask the connector for nearby Bluetooth peripherals and keep only
 * those whose advertised name matches one of the device's known patterns.
 * The scan restarts on its own while `enabled`, so a unit switched on late
 * still shows up.
 */
export function useBluetoothScan(namePatterns: readonly string[], enabled: boolean): BluetoothScanState {
  const [devices, setDevices] = useState<ConnectorBleDevice[]>([])
  const [isScanning, setIsScanning] = useState(false)
  const [hasScanned, setHasScanned] = useState(false)
  const [generation, setGeneration] = useState(0)
  const patternsRef = useRef(namePatterns)
  patternsRef.current = namePatterns

  const rescan = useCallback(() => setGeneration((value) => value + 1), [])

  useEffect(() => {
    if (!enabled) return
    let cancelled = false

    const scanOnce = async () => {
      const startedAt = Date.now()
      setIsScanning(true)
      try {
        const result = await discoverBluetoothDevices(SCAN_TIMEOUT_MS)
        if (cancelled) return
        const matching = result.devices.filter((device) => matchesAnyPattern(device.name, patternsRef.current))
        setDevices((previous) => mergeById(previous, matching))
        setHasScanned(true)
      } catch {
        if (!cancelled) setHasScanned(true)
      } finally {
        if (!cancelled) setIsScanning(false)
      }
      if (cancelled) return
      const elapsed = Date.now() - startedAt
      if (elapsed < MIN_SCAN_INTERVAL_MS) await sleep(MIN_SCAN_INTERVAL_MS - elapsed)
      if (!cancelled) void scanOnce()
    }

    void scanOnce()
    return () => {
      cancelled = true
    }
  }, [enabled, generation])

  return { devices, isScanning, hasScanned, rescan }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function matchesAnyPattern(name: string, patterns: readonly string[]): boolean {
  if (patterns.length === 0) return true
  const lowerName = name.toLowerCase()
  return patterns.some((pattern) => lowerName.includes(pattern.toLowerCase()))
}

/** Keep already-seen units (they may skip a scan window) and refresh their signal strength. */
function mergeById(previous: ConnectorBleDevice[], fresh: ConnectorBleDevice[]): ConnectorBleDevice[] {
  const byId = new Map(previous.map((device) => [device.id, device]))
  for (const device of fresh) byId.set(device.id, device)
  return Array.from(byId.values()).sort((a, b) => (b.rssi ?? -999) - (a.rssi ?? -999))
}
